import type { HelpArticleDef } from "./articleKit";
import {
  AppLink,
  ArticleLink,
  DeploymentNote,
  P,
  Sect,
  Tip,
  Ui,
} from "./articleKit";

export const ADMIN_ARTICLES: HelpArticleDef[] = [
  {
    slug: "your-workspace-url",
    title: "Your workspace URL and signing in",
    category: "Administration",
    minutes: 3,
    summary:
      "Where your CRM lives, why every client gets their own address, and how sign-in and sessions stay isolated to your workspace.",
    keywords: [
      "url", "subdomain", "domain", "address", "login", "sign in",
      "password", "custom domain", "bookmark", "white label",
    ],
    related: ["getting-started", "settings-and-administration", "whats-live-vs-deployment"],
    body: (
      <>
        <P>
          Your deployed workspace lives at its own address:{" "}
          <Ui>yourcompany.obviouscrm.com</Ui>. That one URL is the whole
          story - it's where everyone on your team signs in, works, and
          bookmarks. There's no shared login page and no account picker,
          because the address itself says whose workspace it is.
        </P>
        <Sect title="Why you get your own address">
          <P>
            Every client workspace runs on its own subdomain, and browsers
            treat each subdomain as a separate origin. Your sessions,
            cookies, and cached data live inside your address alone and
            physically can't mix with anyone else's - isolation enforced by
            the browser itself, not by a setting. It also means your data
            lives in your own database, reached only through your address.
          </P>
        </Sect>
        <Sect title="Signing in, and getting locked out">
          <P>
            Sign in on your workspace's own page with your work email. New
            teammates get an invite email that sets their password on that
            same address (see{" "}
            <ArticleLink slug="settings-and-administration">
              users and seats
            </ArticleLink>
            ). Forgot a password? The reset link on the sign-in page sends a
            fresh one to your work email.
          </P>
        </Sect>
        <Sect title="Your own domain, later">
          <P>
            If you'd rather your team sign in at{" "}
            <Ui>crm.yourcompany.com</Ui>, that's supported as a white-label
            option: one DNS record on your side points your name at your
            workspace, and the certificate is handled for you. Ask Grey
            Matter when you're ready.
          </P>
        </Sect>
        <DeploymentNote>
          Your address is registered the moment Grey Matter provisions your
          workspace, and it works immediately - no waiting on DNS. In this
          preview, everything runs at the preview link instead.
        </DeploymentNote>
      </>
    ),
  },
  {
    slug: "settings-and-administration",
    title: "Settings and administration",
    category: "Administration",
    minutes: 6,
    summary:
      "The settings hub: what's personal vs. workspace-wide, users and seats, custom fields, lifecycle, and your plan.",
    keywords: [
      "admin", "users", "seats", "custom fields", "billing", "plan",
      "security", "timezone", "invite", "teammates", "roles", "permissions",
      "limits", "administrator",
    ],
    related: ["getting-started", "your-workspace-url", "importing-your-data"],
    body: (
      <>
        <P>
          Everything configurable lives in one hub at{" "}
          <AppLink to="/settings/profile">Settings</AppLink>, organized in a
          left rail. Each page states its scope up top: personal settings
          affect only you; workspace settings affect everyone.
        </P>
        <Sect title="Your preferences">
          <P>
            <AppLink to="/settings/profile">My profile</AppLink> is your
            name, email, and sign-in details.
          </P>
        </Sect>
        <Sect title="Account management">
          <P>
            <AppLink to="/settings/account-defaults">
              Account defaults
            </AppLink>{" "}
            holds company identity, branding, and the workspace timezone all
            logged times display in.{" "}
            <AppLink to="/settings/signals">Keep an eye on</AppLink> tunes
            the dashboard's{" "}
            <ArticleLink slug="keep-an-eye-on">signal panel</ArticleLink>.
          </P>
        </Sect>
        <Sect title="Users, seats, and teammates">
          <P>
            Invite teammates from{" "}
            <AppLink to="/sales">Users &amp; teams</AppLink>: create the
            user with their name and email, and decide whether they get the{" "}
            <Ui>Administrator</Ui> toggle. Administrators can change
            workspace settings and manage users; regular seats work records,
            reports, and marketing without touching configuration. Each user
            takes one seat against your plan, and a <Ui>Disabled</Ui> toggle
            parks someone without deleting their history.
          </P>
          <DeploymentNote>
            The set-your-password email a new user receives is outbound mail,
            so it sends once your workspace is deployed. In a preview
            workspace, created users appear in the list but can't receive
            the invite.
          </DeploymentNote>
        </Sect>
        <Sect title="Your plan and its limits">
          <P>
            <AppLink to="/settings/billing">Plan &amp; billing</AppLink>{" "}
            shows your seats and contact allowance; the meters turn amber
            near the cap. The cap is real: at the limit, creating another
            contact or user fails with a clear message rather than quietly
            overrunning your plan, and during an{" "}
            <ArticleLink slug="importing-your-data">import</ArticleLink>,
            rows past the cap fail individually. So check the meters before
            a big import or the last seat invite. Limits are managed by Grey
            Matter, and raising them is a quick conversation.
          </P>
        </Sect>
        <Sect title="Data management">
          <P>
            <AppLink to="/settings/objects">Objects</AppLink> shapes your
            records: deal stages and win probabilities, close reasons, task
            types, note statuses.{" "}
            <AppLink to="/settings/lifecycle">Lifecycle stages</AppLink> is
            the funnel ladder contacts climb.{" "}
            <AppLink to="/settings/custom-fields">Custom fields</AppLink>{" "}
            adds workspace-specific fields to contacts, companies, or deals -
            they show on records, in the{" "}
            <ArticleLink slug="forms">form builder</ArticleLink>, and in
            report filters.{" "}
            <AppLink to="/settings/import-export">Import</AppLink> brings
            data in; see{" "}
            <ArticleLink slug="importing-your-data">
              Importing your data
            </ArticleLink>
            .
          </P>
          <Tip>
            Change stages and lifecycle labels early, before the team builds
            habits and reports on them. Renames later are safe but retrain
            everyone's muscle memory.
          </Tip>
        </Sect>
      </>
    ),
  },
  {
    slug: "integrations",
    title: "Integrations: connecting your stack",
    category: "Administration",
    minutes: 5,
    summary:
      "What connects to Obvious, how the OAuth connect flow works, where synced data shows up, and how to disconnect safely.",
    keywords: ["oauth", "google analytics", "search console", "google ads", "linkedin", "calendly", "ai", "api key", "connect"],
    related: ["reports-and-attribution", "inbox-and-email-capture", "whats-live-vs-deployment"],
    body: (
      <>
        <P>
          The{" "}
          <AppLink to="/settings/integrations">Integrations page</AppLink> is
          the one place to see everything Obvious connects to, each with its
          logo, status, and what it feeds. Connections are per workspace and
          per provider; you pick what to connect, and each one is additive.
        </P>
        <Sect title="How connecting works">
          <P>
            Every connection follows the same three steps, spelled out at the
            top of the page. You click <Ui>Connect</Ui> and sign in with the
            provider itself (OAuth; Obvious never sees your password). The
            grant is stored encrypted, scoped to your workspace alone. Then
            data moves on a schedule: analytics and ads refresh nightly into{" "}
            <ArticleLink slug="reports-and-attribution">Reports</ArticleLink>
            ; schedulers and inboxes stream in as events. Disconnecting
            revokes the token with the provider, not just here.
          </P>
        </Sect>
        <Sect title="The catalog">
          <P>
            <Ui>Analytics</Ui>: Google Analytics 4 and Search Console join
            site traffic and search queries against pipeline.{" "}
            <Ui>Advertising</Ui>: Google Ads and LinkedIn Ads bring spend so
            Web &amp; Ads can show true cost per opportunity.{" "}
            <Ui>Scheduling</Ui>: Calendly (or Google booking) lands booked
            meetings on the contact timeline via a webhook.{" "}
            <Ui>Email</Ui>: full inbox sync for Google Workspace and
            Microsoft 365, covered in{" "}
            <ArticleLink slug="inbox-and-email-capture">
              Inbox and email capture
            </ArticleLink>
            . <Ui>AI</Ui>: bring your own Claude or GPT key and the Ask AI
            panel answers questions with your CRM data in hand; keys stay in
            your workspace. <Ui>Forms</Ui>: Cloudflare Turnstile for
            invisible CAPTCHA.
          </P>
        </Sect>
        <DeploymentNote>
          The Connect buttons go live with deployment. The identifier fields
          (GA4 property, Ads customer ID, and so on) save now so each
          integration lights up the moment OAuth lands. Two providers gate
          access on their side (Google Ads developer approval, LinkedIn
          Marketing API partnership); those applications are started early
          because they take the longest.
        </DeploymentNote>
      </>
    ),
  },
];
